import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/Dialog';
import { Button } from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Input } from '../../../components/ui/Input';
import { useServices } from '../../../core/hooks/useServices';
import { useAuth } from '../../../core/contexts/AuthContext';
import type { Client } from '../../../core/models/Client';

interface ServiceAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  client: Client;
  onAssign: (serviceIds: string[], pricingOverrides: Record<string, number>) => Promise<void>;
}

export const ServiceAssignmentDialog: React.FC<ServiceAssignmentDialogProps> = ({
  open,
  onClose,
  client,
  onAssign,
}) => {
  const { organizationId } = useAuth();
  const { services } = useServices(organizationId);
  const [selectedServices, setSelectedServices] = useState<Set<string>>(new Set());
  const [pricingOverrides, setPricingOverrides] = useState<Record<string, number>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleToggleService = (serviceId: string) => {
    const newSelected = new Set(selectedServices);
    if (newSelected.has(serviceId)) {
      newSelected.delete(serviceId);
      const newPricing = { ...pricingOverrides };
      delete newPricing[serviceId];
      setPricingOverrides(newPricing);
    } else {
      newSelected.add(serviceId);
    }
    setSelectedServices(newSelected);
  };

  const handlePricingChange = (serviceId: string, price: number) => {
    setPricingOverrides({
      ...pricingOverrides,
      [serviceId]: price,
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onAssign(Array.from(selectedServices), pricingOverrides);
      setSelectedServices(new Set());
      setPricingOverrides({});
      onClose();
    } catch (error) {
      console.error('Error assigning services:', error);
      alert('Failed to assign services. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Assign Services to {client.name}</DialogTitle>
          <DialogDescription>
            Select services to assign to this project. You can set custom pricing per service.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {services.map((service) => (
            <div
              key={service.id}
              className="flex items-start gap-3 border rounded-lg p-4"
            >
              <Checkbox
                checked={selectedServices.has(service.id)}
                onCheckedChange={() => handleToggleService(service.id)}
              />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium">{service.name}</p>
                    {service.description && (
                      <p className="text-sm text-gray-600">{service.description}</p>
                    )}
                  </div>
                </div>
                {selectedServices.has(service.id) && (
                  <div className="mt-2">
                    <Input
                      label="Custom Price (optional)"
                      type="number"
                      value={pricingOverrides[service.id] || service.defaultRate || ''}
                      onChange={(e) =>
                        handlePricingChange(service.id, parseFloat(e.target.value) || 0)
                      }
                      placeholder={`Default: $${service.defaultRate || 0}`}
                      className="w-32"
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={isSubmitting}>
            Assign Services ({selectedServices.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
